import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetRoundBetsQuery } from '../services/casino/casinoApi';

// Left Arrow Icon
const LeftArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
  </svg>
);

// Right Arrow Icon
const RightArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
  </svg>
);

const formatPlaceTime = (t) => {
  if (!t) return '-';
  // Try parsing as a real date first
  const d = new Date(t);
  if (!isNaN(d)) {
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true,
    });
  }
  // Already a pre-formatted string like "09-06-26 10:20:25" — append AM/PM
  const parts = t.trim().split(' ');
  if (parts.length >= 2) {
    const hour = parseInt(parts[1].split(':')[0], 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${t} ${ampm}`;
  }
  return t;
};

// Fetch casino bets by market ID
const fetchCasinoBetsByMarketId = async function(marketId) {
  try {
    // All integrations disabled
    return { success: false, error: "All integrations have been removed from this project" };
  } catch (err) {
    return { success: false, error: "All integrations have been removed from this project" };
  }
};

const CasinoAllBetsPage = () => {
  const navigate = useNavigate();
  const { roundId } = useParams();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Fetch round bets using RTK Query
  const { data: betsData, isLoading, error: betsError } = useGetRoundBetsQuery(roundId, {
    skip: !roundId,
  });
  
  const bets = betsData?.data || [];
  const summary = betsData?.summary || {
    totalBets: 0,
    wonBets: 0,
    lostBets: 0,
    totalProfit: 0,
    totalLoss: 0,
    pl: 0
  };



  // Pagination
  const totalPages = Math.ceil(bets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBets = bets.slice(startIndex, endIndex);

  const handlePageChange = function(page) {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-[#3e525f] px-5 py-3 flex items-center justify-between">
              <span className="text-xl text-white font-normal">
                All Bets - {roundId}
              </span>
              <button 
                onClick={function() { navigate(-1); }}
                className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
            </div>



            {/* Table */}
            <div className="overflow-x-auto" style={{padding:"10px"}}>
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : betsError ? (
                <div className="p-4 text-red-500 text-center">{betsError?.message || 'Error loading bets'}</div>
              ) : (
                <table className="result-table w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#3e525f] text-white">
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Name</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Runner Name</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Bet Price</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Bet Amount</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Status</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Place Time</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Profit</th>
                      <th className="py-3 px-4 text-left font-medium">Loss</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {currentBets.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-gray-500 border-t border-gray-200">
                          No bets found
                        </td>
                      </tr>
                    ) : (
                      currentBets.map(function(bet, index) {
                        const s = (bet.status || '').toLowerCase();
                        const p = Number(bet.profit) || 0;
                        const resultClass = (s.includes('los') || p < 0) ? 'bet-lost'
                          : (s.includes('won') || s.includes('win') || p > 0) ? 'bet-won' : '';
                        return (
                          <tr key={index} className={`${resultClass} hover:bg-gray-50 border-t border-gray-200`}>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">
                              <div className="font-medium">{bet.fullName || bet.fullname || bet.name || '-'}</div>
                              <div className="text-xs text-gray-500">{bet.username || ''}</div>
                            </td>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">{bet.runnerName || '-'}</td>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">{bet.betPrice ? bet.betPrice.toFixed(2) : '0.00'}</td>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">{bet.betAmount || 0}</td>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">
                              {bet.status || '-'}
                            </td>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">
                              {formatPlaceTime(bet.placeTime)}
                            </td>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">{bet.profit !== undefined && bet.profit !== null ? bet.profit : ''}</td>
                            <td className="py-3 px-4 text-gray-800">{bet.betAmount || ''}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {bets.length > itemsPerPage && (
              <div className="flex justify-end p-3 mx-3">
                <ul className="flex items-center gap-1">
                  <li>
                    <button
                      onClick={function() { handlePageChange(currentPage - 1); }}
                      disabled={currentPage === 1}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <LeftArrowIcon />
                    </button>
                  </li>
                  {Array.from({ length: Math.min(totalPages, 5) }, function(_, i) {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <li key={pageNum}>
                        <button
                          onClick={function() { handlePageChange(pageNum); }}
                          className={
                            'px-3 py-1 rounded ' +
                            (currentPage === pageNum 
                              ? 'bg-blue-500 text-white' 
                              : 'border border-gray-300 hover:bg-gray-100')
                          }
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li>
                    <button
                      onClick={function() { handlePageChange(currentPage + 1); }}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <RightArrowIcon />
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CasinoAllBetsPage;
